import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Pill, Row, Rows } from "~/features/automation/components";
import { budgetWords } from "~/features/knowledge/triggers/budgetWords";
import { Button } from "~/foundation/components/Button";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { type BudgetAccount, parseLimit, useBudgets, useSetBudget } from "./budgetsApi";

/**
 * BRIEF-CGNI S2-2 — Thinkers → Usage → Budgets. Every payer of this workspace: its pool, each project's allocation inside it,
 * each person's lease. A limit is either ENFORCED (a call that would pass it waits, and says until when) or in shadow (the
 * call runs; the page shows what it would have refused). Admins allocate — never above the plan, a project never above its pool.
 */
export function BudgetsPanel() {
  const { t, i18n } = useTranslation();
  const budgets = useBudgets();
  const [editing, setEditing] = useState<string | null>(null);
  if (budgets.isLoading) return <Spinner label={t("Loading")} />;
  if (budgets.error) return <Text variant="small">{(budgets.error as Error).message}</Text>;
  const b = budgets.data;
  if (!b) return null;
  const lang = i18n.language;
  const date = (iso: string) =>
    new Date(iso).toLocaleDateString(lang, { day: "numeric", month: "long" });
  const n = (value: number) => value.toLocaleString(lang);
  const rows = [b.workspace, ...b.projects, ...b.people];

  return (
    <section className="flex flex-col gap-3" data-testid="budgets-panel">
      <div className="flex flex-col gap-1">
        <p className="text-body font-medium text-text">{t("Budgets")}</p>
        <Text variant="small">
          {b.plan
            ? t(
                "{{plan}} plan · {{perSeat}} tokens a seat a month · {{count}} seats · renews on {{date}}",
                {
                  plan: b.plan,
                  perSeat: b.perSeat == null ? t("unlimited") : n(b.perSeat),
                  count: b.seats,
                  date: date(b.resetsAt),
                },
              )
            : t("No plan is recorded for this workspace · the month renews on {{date}}", {
                date: date(b.resetsAt),
              })}
        </Text>
      </div>
      <Rows>
        {rows.map((a) => (
          <Row key={`${a.kind}-${a.subjectId}`} className="flex-col items-stretch gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <p className="min-w-0 flex-1 text-body text-text [overflow-wrap:anywhere]">
                {a.kind === "workspace" ? t("This workspace") : a.name}
                {a.mine ? <span className="text-text-tertiary"> · {t("you")}</span> : null}
              </p>
              <Pill>
                {t(a.kind === "workspace" ? "pool" : a.kind === "project" ? "project" : "person")}
              </Pill>
              <Pill
                tone={
                  a.state === "exhausted" || a.state === "none"
                    ? "danger"
                    : a.state === "near"
                      ? "warning"
                      : "success"
                }
              >
                {a.state === "exhausted"
                  ? t("used up")
                  : a.state === "none"
                    ? t("no allowance")
                    : a.state === "near"
                      ? t("nearly used")
                      : t("within budget")}
              </Pill>
              {a.limit != null || a.dailyLimit != null ? (
                <Pill tone={a.enforced ? "accent" : "neutral"}>
                  {a.enforced ? t("enforced") : t("shadow")}
                </Pill>
              ) : null}
            </div>
            <Text variant="small">
              {a.limit == null
                ? t("{{used}} tokens this month · no limit", { used: n(a.used + a.held) })
                : t("{{used}} of {{limit}} tokens this month", {
                    used: n(a.used + a.held),
                    limit: n(a.limit),
                  })}
              {a.held > 0 ? ` · ${t("{{held}} held by calls in flight", { held: n(a.held) })}` : ""}
              {" · "}
              {t("{{today}} today", { today: n(a.today) })}
              {a.dailyLimit != null
                ? ` · ${t("{{daily}} a day, renews at 00:00 UTC", { daily: n(a.dailyLimit) })}`
                : ""}
            </Text>
            {a.wouldHaveWaited && !a.enforced ? (
              <Text variant="small">
                {t("Shadow — would have waited")}:{" "}
                {budgetWords(t, a.wouldHaveWaited, i18n.language) ?? a.wouldHaveWaited}
              </Text>
            ) : null}
            {a.setByName && a.setAt ? (
              <Text variant="small">
                {t("Last changed by {{who}} on {{date}}", {
                  who: a.setByName,
                  date: date(a.setAt),
                })}
              </Text>
            ) : null}
            {b.admin ? (
              editing === `${a.kind}-${a.subjectId}` ? (
                <AllocationForm account={a} onDone={() => setEditing(null)} />
              ) : (
                <div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing(`${a.kind}-${a.subjectId}`)}
                  >
                    {t("Allocate")}
                  </Button>
                </div>
              )
            ) : null}
          </Row>
        ))}
      </Rows>
      {b.uncertain > 0 ? (
        <Text variant="small">
          {t(
            "{{count}} call ended without saying what it used; {{tokens}} tokens stay counted until it is reconciled.",
            {
              count: b.uncertain,
              tokens: n(b.uncertainTokens),
            },
          )}
        </Text>
      ) : null}
      {b.withoutContext.length > 0 ? (
        <div className="flex flex-col gap-1">
          <Text variant="small">
            {t(
              "Calls that did not say whose work they were (charged to the pool until their runner says so)",
            )}
          </Text>
          <Rows>
            {b.withoutContext.map((s) => (
              <Row key={s.source}>
                <p className="min-w-0 flex-1 truncate text-small text-text">{s.source}</p>
                <span className="text-small text-text-secondary">
                  {t("{{n}} calls · {{tokens}} tokens", { n: s.calls, tokens: n(s.tokens) })}
                </span>
              </Row>
            ))}
          </Rows>
        </div>
      ) : null}
      {b.refusals.length > 0 ? (
        <div className="flex flex-col gap-1">
          <Text variant="small">{t("Waited for budget this month")}</Text>
          <Rows>
            {b.refusals.map((r) => (
              <Row key={`${r.at}-${r.sentence}`}>
                <span className="w-24 shrink-0 text-small text-text-tertiary">{date(r.at)}</span>
                <p className="min-w-0 flex-1 text-small text-text [overflow-wrap:anywhere]">
                  {budgetWords(t, r.sentence, i18n.language) ?? r.sentence}
                </p>
                <Pill tone={r.enforced ? "warning" : "neutral"}>
                  {r.enforced ? t("waited") : t("shadow")}
                </Pill>
              </Row>
            ))}
          </Rows>
        </div>
      ) : null}
    </section>
  );
}

function AllocationForm({ account, onDone }: { account: BudgetAccount; onDone: () => void }) {
  const { t } = useTranslation();
  const set = useSetBudget();
  const [monthly, setMonthly] = useState(
    account.allocation == null ? "" : String(account.allocation),
  );
  const [daily, setDaily] = useState(account.dailyLimit == null ? "" : String(account.dailyLimit));
  const [enforce, setEnforce] = useState(account.enforced);
  const submit = () => {
    const m = parseLimit(monthly);
    const d = parseLimit(daily);
    if (m === "invalid" || d === "invalid") {
      toast.error(t("A limit is a whole number of tokens, or empty for none."));
      return;
    }
    set.mutate(
      { kind: account.kind, subjectId: account.subjectId, monthlyLimit: m, dailyLimit: d, enforce },
      { onSuccess: onDone, onError: (e) => toast.error((e as Error).message) },
    );
  };
  return (
    <div
      className="flex flex-col gap-2 rounded-md bg-bg-secondary p-2"
      data-testid="allocation-form"
    >
      <div className="flex flex-wrap gap-2">
        <Input
          label={t("Tokens a month (empty = only the plan's limit)")}
          inputMode="numeric"
          value={monthly}
          onChange={(e) => setMonthly(e.target.value)}
        />
        <Input
          label={t("Tokens a day (empty = no daily cap)")}
          inputMode="numeric"
          value={daily}
          onChange={(e) => setDaily(e.target.value)}
        />
      </div>
      <Switch
        checked={enforce}
        onCheckedChange={setEnforce}
        label={t("Enforce")}
        description={t("Off: calls run and the page shows what would have waited.")}
      />
      <div className="flex gap-2">
        <Button variant="primary" size="sm" disabled={set.isPending} onClick={submit}>
          {t("Save")}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
}
